import React, { ReactNode, useEffect } from 'react';
import {
    Navbar,
    NavbarBrand,
    NavbarToggler,
    Collapse,
    Nav,
    NavItem
} from 'reactstrap';
import DocumentHead from './DocumentHead';
import useBotPagesConfig from '../../hooks/useBotPagesConfig';
import { Link, NavLink } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import joinPath from '../../lib/joinPath.ts';

const Layout: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const toggle = () => setIsOpen(!isOpen);

    const {
        title: configTitle,
        hasArticles,
        homePageUrl,
        goToHomePageTitle,
        styles,
        subscriptionKey,
        portalScriptUrl,
        hasErrors,
        requiresAuthentication
    } = useBotPagesConfig();

    const { instance } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const loadBot = isAuthenticated || !requiresAuthentication;

    const divId = `bot-id-${subscriptionKey}`;
    const botScriptUrl =
        hasErrors || !loadBot
            ? ''
            : joinPath(
                  portalScriptUrl,
                  `/api/v1/scripts/${subscriptionKey}/js/corejs`
              );

    useEffect(() => {
        if (!document.querySelector(`script[src="${botScriptUrl}"]`)) {
            const script = document.createElement('script');
            script.src = botScriptUrl;
            script.async = true;
            document.body.appendChild(script);
        }
    }, [botScriptUrl]);

    const signOut = () => {
        instance.logoutPopup();
    };

    React.useEffect(() => {
        document.body.style.fontFamily = styles?.fontFamily;

        const root = document.documentElement;
        root.style.setProperty('--primary', styles?.primaryColor);
        root.style.setProperty('--primary-light', styles?.primaryColorLight);
        root.style.setProperty(
            '--primary-lighter',
            styles?.primaryColorLighter
        );
        root.style.setProperty('--primary-dark', styles?.primaryColorDark);
        root.style.setProperty('--text-color', styles?.fontColor);
        root.style.setProperty(
            '--hero-overlay',
            styles?.backgroundOverlayColor
        );
        root.style.setProperty(
            '--background-url',
            `url('${styles?.backgroundImageUrl}')`
        );
    }, [styles]);

    if (hasErrors) {
        return (
            <h1>
                <a href='https://helpfruit.com'>Helpfruit Pages</a>
            </h1>
        );
    }

    return (
        <>
            <DocumentHead />
            <Navbar expand='lg' className=' bg-light'>
                <NavbarBrand
                    tag={Link}
                    to='/'
                    className='d-flex align-items-center me-0'
                >
                    <img src={styles?.logoUrl} alt='' className='me-2' />
                    <span className='product-name'>
                        {configTitle || 'Help Pages'}
                    </span>
                </NavbarBrand>

                <NavbarToggler onClick={toggle} className='' />

                <Collapse isOpen={isOpen} navbar className=''>
                    <Nav navbar className=''>
                        <NavItem>
                            <NavLink
                                to='/assistant'
                                className='text-primary cursor-pointer px-3'
                            >
                                Assistant
                            </NavLink>
                        </NavItem>

                        {hasArticles && (
                            <NavItem>
                                <NavLink
                                    to='/articles'
                                    className='text-primary cursor-pointer px-3'
                                >
                                    Articles
                                </NavLink>
                            </NavItem>
                        )}

                        <NavItem>
                            <NavLink
                                to={homePageUrl}
                                className='text-primary fw-bold cursor-pointer px-3'
                                target='_self'
                            >
                                {goToHomePageTitle || 'Go to main page'}
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 448 512'
                                    fill='currentColor'
                                    width='1rem'
                                    height='1rem'
                                    className='ms-1'
                                >
                                    <path d='M288 32c-17.7 0-32 14.3-32 32s14.3 32 32 32h50.7L169.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L384 141.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V64c0-17.7-14.3-32-32-32H288zM80 64C35.8 64 0 99.8 0 144V400c0 44.2 35.8 80 80 80H336c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32v80c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V144c0-8.8 7.2-16 16-16h80c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z' />
                                </svg>
                            </NavLink>
                        </NavItem>

                        {isAuthenticated && (
                            <NavItem>
                                <a
                                    href='#'
                                    aria-current='page'
                                    onClick={(event) => (
                                        event.preventDefault(), signOut()
                                    )}
                                    className='text-primary fw-bold cursor-pointer px-3 active'
                                >
                                    Sign out
                                </a>
                            </NavItem>
                        )}
                    </Nav>
                </Collapse>
            </Navbar>

            <main role='main'>{children}</main>

            <footer className='w-100 bg-white text-center p-2'>
                Help Pages created by{' '}
                <a
                    href='https://www.helpfruit.com'
                    target='_blank'
                    rel='noopener noreferrer'
                    title='Helpfruit - Chatbot, Help Pages, Live Chat'
                >
                    Helpfruit
                </a>
            </footer>
            {loadBot && <div id={divId}></div>}
        </>
    );
};

export default Layout;
